import { useAuth } from "@/features/auth";
import { useDossierTraceId } from "@/shared/hooks/use-dossier-trace-id.tsx";
import { generateSpanId, generateTraceId, getEnv, withApiDomain } from "@/shared/utils";
import { useCallback, useMemo } from "react";

const backendUrl = getEnv("VITE_BACKEND_URL");
const API_BASE_URL = backendUrl ? `${backendUrl}/api` : `${window.location.origin}/api`;

type RequestData = Record<string, unknown> | number[] | Record<string, unknown>[] | Array<unknown>;

export class ApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
        this.name = "ApiError";
    }
}

/**
 * Dispatch an API error event for global error handling
 */
const dispatchApiErrorEvent = (status: number, message: string) => {
    const event = new CustomEvent("api-error", {
        detail: {
            status,
            message,
        },
    });
    window.dispatchEvent(event);
};

async function fetchAndHandleError(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = new Headers(options.headers);

    /**
     * Although confusing, x-datadog-trace-id is the "root" trace that we can link multiple spans to.
     * The x-datadog-parent-id is unique per request
     */
    headers.set("x-datadog-parent-id", generateSpanId());
    headers.set("x-datadog-sampling-priority", "1");

    const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
    const response = await fetch(fullUrl, {
        ...options,
        headers,
    });

    if (!response.ok) {
        let errorMessage = "Aanvraag mislukt";
        try {
            errorMessage = await response.text();
        } catch (error) {
            console.error("Failed to parse response", error);
        }

        // Dispatch event for authentication errors
        if (response.status === 401) {
            dispatchApiErrorEvent(401, errorMessage);
        }

        throw new ApiError(errorMessage, response.status);
    }
    return response;
}

// access token should be included in the `options` parameter as header, or you'll get a 401 Unauthorized error
// the preferred way to use this is through the `useApiClient` hook
async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T | null> {
    const response = await fetchAndHandleError(url, options);
    const contentLength = response.headers.get("content-length");
    const contentType = response.headers.get("content-type");

    if (response.status === 204 || contentLength === "0" || !contentType?.includes("application/json")) {
        return null;
    }

    try {
        return (await response.json()) as T;
    } catch (error) {
        console.error("Failed to parse response", error);
        throw new ApiError("Failed to parse response", 500);
    }
}

export type DownloadedFile = {
    blob: Blob;
    fileName: string;
};

/** A request that carries no body, e.g. GET or DELETE. */
type BodylessRequest = <T>(url: string, options?: RequestInit) => Promise<T | null>;

/** A request that sends a JSON body, e.g. POST, PUT or PATCH. */
type JsonBodyRequest = <T>(url: string, data?: RequestData, options?: RequestInit) => Promise<T | null>;

/** Authenticated API client returned by {@link useApiClient}. */
export type ApiClient = {
    get: BodylessRequest;
    downloadFile: (url: string, options?: RequestInit) => Promise<DownloadedFile | null>;
    post: JsonBodyRequest;
    uploadFile: <T>(url: string, file: File, options?: RequestInit) => Promise<T | null>;
    put: JsonBodyRequest;
    patch: JsonBodyRequest;
    delete: BodylessRequest;
};

async function fetchFileBlob(url: string, options: RequestInit = {}): Promise<DownloadedFile | null> {
    const response = await fetchAndHandleError(url, options);
    const contentDisposition = response.headers.get("content-disposition");
    const fileName = contentDisposition?.match(/filename="(.+)"/)?.[1] ?? "download.pdf";

    try {
        const blob = await response.blob();
        return { blob, fileName };
    } catch (err) {
        console.error(err);
        throw err;
    }
}

/**
 * Hook-based API client for use in React components
 */
export const useApiClient = (): ApiClient => {
    const { getAccessToken, apiDomain } = useAuth();
    const prefixUrl = withApiDomain(apiDomain);

    // "root" trace id based on the current route
    const dossierTraceId = useDossierTraceId();

    const get = useCallback(
        async <T>(url: string, options?: RequestInit): Promise<T | null> => {
            const accessToken = await getAccessToken();
            return fetchJson<T>(prefixUrl(url), {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": dossierTraceId,
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                method: "GET",
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    const downloadFile = useCallback(
        async (url: string, options?: RequestInit): Promise<DownloadedFile | null> => {
            const accessToken = await getAccessToken();
            return fetchFileBlob(prefixUrl(url), {
                ...options,
                headers: { ...options?.headers, "x-datadog-trace-id": dossierTraceId, Authorization: `Bearer ${accessToken}` },
                method: "GET",
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    const post = useCallback(
        async <T>(url: string, data?: RequestData, options?: RequestInit): Promise<T | null> => {
            const accessToken = await getAccessToken();
            return fetchJson<T>(prefixUrl(url), {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": dossierTraceId,
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                method: "POST",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    const uploadFile = useCallback(
        async <T>(url: string, file: File, options?: RequestInit): Promise<T | null> => {
            const accessToken = await getAccessToken();
            const formData = new FormData();
            formData.append("file", file);

            return fetchJson<T>(prefixUrl(url), {
                ...options,
                // FormData (used for file uploads) automatically gets multipart/form-data with a boundary as Content-Type from the browser
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": dossierTraceId,
                    Authorization: `Bearer ${accessToken}`,
                },
                method: "POST",
                body: formData,
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    const put = useCallback(
        async <T>(url: string, data?: RequestData, options?: RequestInit): Promise<T | null> => {
            const accessToken = await getAccessToken();
            return fetchJson<T>(prefixUrl(url), {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": dossierTraceId,
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                method: "PUT",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    const patch = useCallback(
        async <T>(url: string, data?: RequestData, options?: RequestInit): Promise<T | null> => {
            const accessToken = await getAccessToken();
            return fetchJson<T>(prefixUrl(url), {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": dossierTraceId,
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                method: "PATCH",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    const del = useCallback(
        async <T>(url: string, options?: RequestInit): Promise<T | null> => {
            const accessToken = await getAccessToken();
            return fetchJson<T>(prefixUrl(url), {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": dossierTraceId,
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                method: "DELETE",
            });
        },
        [getAccessToken, prefixUrl, dossierTraceId]
    );

    return {
        get,
        downloadFile,
        post,
        uploadFile,
        put,
        patch,
        delete: del,
    };
};

/**
 * Hook-based API client for use in React components
 */
export const useUnauthenticatedApiClient = () => {
    // "root" trace id based on the current route
    const routeTraceId = useMemo(() => generateTraceId(), []);

    const get = useCallback(
        async <T>(url: string, options?: RequestInit): Promise<T | null> => {
            return fetchJson<T>(url, {
                ...options,
                headers: { ...options?.headers, "x-datadog-trace-id": routeTraceId, "Content-Type": "application/json" },
                method: "GET",
            });
        },
        [routeTraceId]
    );

    const post = useCallback(
        async <T>(url: string, data?: RequestData, options?: RequestInit): Promise<T | null> => {
            return fetchJson<T>(url, {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": routeTraceId,
                    "Content-Type": "application/json",
                },
                method: "POST",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [routeTraceId]
    );

    const put = useCallback(
        async <T>(url: string, data?: RequestData, options?: RequestInit): Promise<T | null> => {
            return fetchJson<T>(url, {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": routeTraceId,
                    "Content-Type": "application/json",
                },
                method: "PUT",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [routeTraceId]
    );

    const patch = useCallback(
        async <T>(url: string, data?: RequestData, options?: RequestInit): Promise<T | null> => {
            return fetchJson<T>(url, {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": routeTraceId,
                    "Content-Type": "application/json",
                },
                method: "PATCH",
                body: data ? JSON.stringify(data) : undefined,
            });
        },
        [routeTraceId]
    );

    const del = useCallback(
        async <T>(url: string, options?: RequestInit): Promise<T | null> => {
            return fetchJson<T>(url, {
                ...options,
                headers: {
                    ...options?.headers,
                    "x-datadog-trace-id": routeTraceId,
                    "Content-Type": "application/json",
                },
                method: "DELETE",
            });
        },
        [routeTraceId]
    );

    return {
        get,
        post,
        put,
        patch,
        delete: del,
    };
};
